import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  CreditCard, Check, AlertTriangle, Clock, 
  ExternalLink, RefreshCw, ArrowRight, Building2,
  Shield, CheckCircle2, XCircle, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/utils/api';
import { useTenant } from '@/contexts/TenantContext';

// ImpactGolf is the platform's master/fulfillment-source tenant - it sells and is
// paid directly. Every other tenant is a reseller storefront that earns a
// commission from Golfshop.fit rather than being paid directly by their own
// customers, so this page's copy is worded differently for them.
const IMPACT_GOLF_TENANT_ID = 'tenant_2a7782078b27';

export default function PaymentsSetupPage() {
  const [searchParams] = useSearchParams();
  const { tenant } = useTenant();
  const isImpactGolf = tenant?.tenant_id === IMPACT_GOLF_TENANT_ID;
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [connectStatus, setConnectStatus] = useState(null);

  const loadConnectStatus = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/connect/status');
      setConnectStatus(response.data);
    } catch (error) {
      console.error('Failed to load connect status:', error);
      toast.error('Failed to load payment setup status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Check for onboarding return
    if (searchParams.get('onboarding') === 'complete') {
      toast.success('Welcome back! Let us check your onboarding status...');
    }
    if (searchParams.get('refresh') === 'true') {
      toast.info('Please continue setting up your payment account.');
    }
    
    loadConnectStatus();
  }, [searchParams, loadConnectStatus]);

  const handleStartOnboarding = async () => {
    setActionLoading(true);
    try {
      const response = await api.post('/connect/create-account', {
        origin_url: window.location.origin
      });
      
      if (response.data.onboarding_url) {
        toast.success('Redirecting to Stripe...');
        window.location.href = response.data.onboarding_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start onboarding');
    } finally {
      setActionLoading(false);
    }
  };

  const handleContinueOnboarding = async () => {
    setActionLoading(true);
    try {
      const response = await api.post('/connect/create-account-link', {
        origin_url: window.location.origin
      });
      
      if (response.data.onboarding_url) {
        toast.success('Redirecting to Stripe...');
        window.location.href = response.data.onboarding_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to continue onboarding');
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenDashboard = async () => {
    setActionLoading(true);
    try {
      const response = await api.post('/connect/create-dashboard-link');
      
      if (response.data.dashboard_url) {
        window.open(response.data.dashboard_url, '_blank');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open dashboard');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('This clears your current Stripe connection so you can set it up again. Continue?')) {
      return;
    }
    setActionLoading(true);
    try {
      await api.post('/connect/disconnect');
      toast.success('Stripe connection cleared. You can set it up again now.');
      await loadConnectStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset Stripe connection');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = () => {
    if (!connectStatus) return null;
    
    const { connect_status } = connectStatus;
    
    switch (connect_status) {
      case 'complete':
        return (
          <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
            <CheckCircle2 className="h-4 w-4" />
            Active
          </div>
        );
      case 'pending':
        return (
          <div className="inline-flex items-center gap-2 bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-sm font-medium">
            <Clock className="h-4 w-4" />
            Pending
          </div>
        );
      case 'restricted':
        return (
          <div className="inline-flex items-center gap-2 bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-medium">
            <AlertTriangle className="h-4 w-4" />
            Restricted
          </div>
        );
      default:
        return (
          <div className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-medium">
            <Clock className="h-4 w-4" />
            Not Started
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const isComplete = connectStatus?.connect_status === 'complete';
  const isPending = connectStatus?.connect_status === 'pending';
  const isRestricted = connectStatus?.connect_status === 'restricted';
  const notStarted = connectStatus?.connect_status === 'not_started';

  return (
    <div className="space-y-6" data-testid="payments-setup-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Payments Setup</h1>
        <p className="text-gray-500">
          {isImpactGolf
            ? 'Connect your Stripe account to accept payments'
            : 'Connect your Stripe account to receive your commission payments from Golfshop.fit'}
        </p>
      </div>

      {/* Status Card */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Stripe Connect</h2>
            {getStatusBadge()}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={loadConnectStatus}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Not Started State */}
        {notStarted && (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CreditCard className="h-8 w-8 text-blue-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Set Up Payments</h3>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              {isImpactGolf ? (
                <>
                  Connect your Stripe account to start accepting payments from customers.
                  This only takes a few minutes.
                </>
              ) : (
                <>
                  Connect your Stripe account to start receiving commission payments from
                  Golfshop.fit. This only takes a few minutes.
                </>
              )}
            </p>
            <Button
              onClick={handleStartOnboarding}
              disabled={actionLoading}
              className="bg-[#4A6741] hover:bg-[#3d5636]"
              data-testid="start-onboarding-btn"
            >
              {actionLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <ArrowRight className="h-4 w-4 mr-2" />
              )}
              Start Setup
            </Button>
          </div>
        )}

        {/* Pending State */}
        {isPending && (
          <div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div>
                  <p className="font-medium text-yellow-800">Setup Incomplete</p>
                  <p className="text-sm text-yellow-700">
                    {isImpactGolf
                      ? 'Your payment setup is not complete. Please continue the onboarding process to start accepting payments.'
                      : 'Your payment setup is not complete. Please continue the onboarding process to start receiving your commission payments.'}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Status Details */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                {connectStatus?.charges_enabled ? (
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                ) : (
                  <XCircle className="h-5 w-5 text-gray-400" />
                )}
                <div>
                  <p className="text-sm font-medium text-gray-900">Card Payments</p>
                  <p className="text-xs text-gray-500">
                    {connectStatus?.charges_enabled ? 'Enabled' : 'Not enabled'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                {connectStatus?.payouts_enabled ? (
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                ) : (
                  <XCircle className="h-5 w-5 text-gray-400" />
                )}
                <div>
                  <p className="text-sm font-medium text-gray-900">Payouts</p>
                  <p className="text-xs text-gray-500">
                    {connectStatus?.payouts_enabled ? 'Enabled' : 'Not enabled'}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={handleContinueOnboarding}
                disabled={actionLoading}
                className="bg-[#4A6741] hover:bg-[#3d5636]"
                data-testid="continue-onboarding-btn"
              >
                {actionLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <ArrowRight className="h-4 w-4 mr-2" />
                )}
                Continue Setup
              </Button>
              <Button
                variant="outline"
                onClick={handleDisconnect}
                disabled={actionLoading}
                data-testid="disconnect-stripe-btn-pending"
              >
                Start Over Instead
              </Button>
            </div>
          </div>
        )}

        {/* Restricted State */}
        {isRestricted && (
          <div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                <div>
                  <p className="font-medium text-red-800">Action Needed</p>
                  <p className="text-sm text-red-700">
                    Stripe has restricted this connected account, or the connection is no longer
                    valid (this can happen after a platform key change). Click below to clear it
                    and set up payments again - it only takes a few minutes.
                  </p>
                </div>
              </div>
            </div>

            <Button
              onClick={handleDisconnect}
              disabled={actionLoading}
              className="bg-[#4A6741] hover:bg-[#3d5636]"
              data-testid="disconnect-stripe-btn"
            >
              {actionLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Disconnect &amp; Start Over
            </Button>
          </div>
        )}

        {/* Complete State */}
        {isComplete && (
          <div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <p className="font-medium text-green-800">Payment Setup Complete</p>
                  <p className="text-sm text-green-700">
                    {isImpactGolf
                      ? 'Your store is ready to accept payments. Customers can now checkout and pay for their orders.'
                      : "You're all set to receive commission payments from Golfshop.fit based on your store's sales."}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Status Details */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Card Payments</p>
                  <p className="text-xs text-gray-500">Enabled</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Payouts</p>
                  <p className="text-xs text-gray-500">Enabled</p>
                </div>
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleOpenDashboard}
                disabled={actionLoading}
                data-testid="open-stripe-dashboard-btn"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Open Stripe Dashboard
              </Button>
              
              <Button
                variant="outline"
                onClick={handleContinueOnboarding}
                disabled={actionLoading}
              >
                <Building2 className="h-4 w-4 mr-2" />
                Update Account Info
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Info Cards */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Shield className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900 mb-1">Secure Payments</h3>
              <p className="text-sm text-gray-500">
                All payments are processed securely through Stripe. Your customers' payment information is never stored on our servers.
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CreditCard className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900 mb-1">
                {isImpactGolf ? 'Direct Payouts' : 'Commission Payouts'}
              </h3>
              <p className="text-sm text-gray-500">
                {isImpactGolf
                  ? "Customer payments go directly to your connected Stripe account. Funds are available according to your Stripe payout schedule."
                  : "Golfshop.fit collects customer payments and pays your commission to your connected Stripe account according to your payout schedule."}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Frequently Asked Questions</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-900">What information do I need to provide?</h3>
            <p className="text-sm text-gray-600">
              Stripe requires basic business information, bank account details for payouts, and identity verification. 
              This is required by financial regulations.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-900">How long does setup take?</h3>
            <p className="text-sm text-gray-600">
              Most accounts can complete setup in 5-10 minutes. Some accounts may require additional verification 
              which can take 1-2 business days.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-900">When will I receive payouts?</h3>
            <p className="text-sm text-gray-600">
              {isImpactGolf ? (
                <>
                  Once set up, payouts are typically sent to your bank account on a rolling basis (e.g., 2 days after payment).
                  You can manage your payout schedule in the Stripe dashboard.
                </>
              ) : (
                <>
                  Once set up, your commission payments are sent to your connected Stripe account and paid out to your
                  bank account according to your payout schedule. You can manage your payout schedule in the Stripe dashboard.
                </>
              )}
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-900">What fees apply?</h3>
            <p className="text-sm text-gray-600">
              {isImpactGolf ? (
                <>
                  Standard Stripe processing fees apply (2.9% + $0.30 per transaction). A small platform fee
                  is also deducted to support Golfshop.fit Platform.
                </>
              ) : (
                <>
                  Golfshop.fit covers standard Stripe processing fees on customer orders. Your commission is
                  paid out based on your store's agreed commission rate — no processing fees are deducted from your payout.
                </>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
